import { useQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Building2, Plus } from 'lucide-react'
import { useState, type FormEvent } from 'react'
import { godMeQuery, logoSrc, lookupForGod, orgsQuery, useCreateOrg, useUploadLogo } from '@/api/queries'
import type { Organization } from '@/api/types'
import { EmailLookup, type Found } from '@/app/email-lookup'
import { LogoPicker, type PickedLogo } from '@/app/logo-picker'
import { NotForYou } from '@/app/not-for-you'
import { errorText } from '@/domain/errors'
import { formatDate, t } from '@/i18n'
import { cn } from '@/lib/cn'
import { Button } from '@/ui/button'
import { Checkbox } from '@/ui/checkbox'
import { CompanyLogo } from '@/ui/company-logo'
import { DataList, type DataColumn } from '@/ui/data-list'
import { Field } from '@/ui/field'
import { InlineBanner } from '@/ui/inline-banner'
import { SidePanel } from '@/ui/overlays'
import { EmptyState, Page, PageHeader } from '@/ui/page'
import { SettingsSection } from '@/ui/settings'
import { LoadingBlock } from '@/ui/spinner'

export const Route = createFileRoute('/_app/gods/companies')({
  component: Companies,
})

/// Gods' console · Компанії: every organisation on eatlete, and the only
/// place a company is born (G1). The god names the first owner by email and
/// does not become a member.
function Companies() {
  const navigate = useNavigate()
  const god = useQuery(godMeQuery())
  const orgs = useQuery(orgsQuery())
  const [creating, setCreating] = useState(false)
  /// The company just created, and — when its logo did not make it — why.
  const [created, setCreated] = useState<{ org: Organization; logoError: string | null } | null>(
    null,
  )

  if (god.data === false) return <NotForYou />

  const open = (org: Organization) => void navigate({ to: '/c/$orgId', params: { orgId: org.id } })
  const openSettings = (org: Organization) =>
    void navigate({ to: '/c/$orgId/settings', params: { orgId: org.id } })

  return (
    <Page wide>
      <PageHeader
        title={t.companies.title}
        meta={orgs.data ? String(orgs.data.length) : undefined}
        subtitle={t.companies.subtitle}
        actions={
          <Button variant="primary" icon={<Plus className="size-[18px]" strokeWidth={2.5} />} onClick={() => setCreating(true)}>
            {t.companies.create}
          </Button>
        }
      />

      {created?.logoError ? (
        // The company exists; only the picture is missing. Said so plainly,
        // with the way to fix it — the form is not lost, there is nothing
        // left in it to redo.
        <InlineBanner
          kind="stale"
          className="mb-6"
          title={t.companies.logoFailedTitle(created.org.name)}
          message={t.companies.logoFailedMessage(created.logoError)}
          action={{ label: t.companies.openSettings, onClick: () => openSettings(created.org) }}
        />
      ) : created ? (
        <InlineBanner
          kind="done"
          className="mb-6"
          message={t.companies.createdBanner(created.org.name)}
          action={{ label: t.companies.open, onClick: () => open(created.org) }}
        />
      ) : null}

      {orgs.isPending ? (
        <LoadingBlock label={t.app.loading} />
      ) : orgs.isError ? (
        <InlineBanner
          kind="error"
          message={errorText(orgs.error)}
          action={{ label: t.app.retry, onClick: () => void orgs.refetch() }}
        />
      ) : orgs.data.length === 0 ? (
        <EmptyState
          icon={<Building2 />}
          title={t.companies.empty}
          message={t.companies.emptyMessage}
        />
      ) : (
        <DataList
          label={t.companies.title}
          columns={companyColumns}
          rows={orgs.data}
          rowKey={(org) => org.id}
          rowTarget={(org) => ({ to: '/c/$orgId', params: { orgId: org.id } })}
          rowLabel={(org) => `${t.companies.open}: ${org.name}`}
          rowTone={(org) => (org.status === 'active' ? 'normal' : 'muted')}
        />
      )}

      <CreateCompanyPanel
        open={creating}
        onOpenChange={setCreating}
        onCreated={(org, logoError) => {
          setCreated({ org, logoError })
          setCreating(false)
        }}
      />
    </Page>
  )
}

/// Every company as a row of facts: name with its mark, how many clubs, open
/// or suspended, since when. The row opens the company.
const companyColumns: readonly DataColumn<Organization>[] = [
  {
    key: 'name',
    header: t.companies.columns.name,
    width: 'minmax(0,2fr)',
    cell: (org) => (
      <span className="flex min-w-0 items-center gap-2.5">
        <CompanyLogo name={org.name} src={logoSrc(org.logoUrl)} size="sm" className="size-7 rounded-[7px] text-[11px]" />
        <span className="truncate">{org.name}</span>
      </span>
    ),
  },
  {
    key: 'status',
    header: t.companies.columns.status,
    width: '128px',
    mobile: 'trailing',
    cell: (org) => (
      <span className={cn('inline-flex items-center gap-1.5', org.status === 'active' ? 'text-text-2' : 'text-warning')}>
        <span aria-hidden="true" className={cn('size-1.5 rounded-full', org.status === 'active' ? 'bg-success' : 'bg-warning')} />
        {t.companies.status[org.status] ?? org.status}
      </span>
    ),
  },
  {
    key: 'branches',
    header: t.companies.columns.branches,
    width: '96px',
    numeric: true,
    cell: (org) => (org.branchCount === undefined ? '—' : org.branchCount === 0 ? <span className="text-muted">—</span> : String(org.branchCount)),
  },
  {
    key: 'created',
    header: t.companies.columns.created,
    width: '120px',
    numeric: true,
    hideBelow: 'md',
    mobile: 'hidden',
    cell: (org) => formatDate(org.createdAt),
  },
]

type CompanyErrors = Partial<Record<'name' | 'owner' | 'branch', string | undefined>>

function CreateCompanyPanel({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  /// `logoError` is set when the company was created but its logo was not.
  onCreated: (org: Organization, logoError: string | null) => void
}) {
  const create = useCreateOrg()
  const upload = useUploadLogo()
  const [logo, setLogo] = useState<PickedLogo | null>(null)
  const [name, setName] = useState('')
  const [owner, setOwner] = useState<Found | null>(null)
  const [withBranch, setWithBranch] = useState(true)
  const [branchName, setBranchName] = useState('')
  const [city, setCity] = useState('')
  const [address, setAddress] = useState('')
  const [errors, setErrors] = useState<CompanyErrors>({})

  const reset = () => {
    setName('')
    setOwner(null)
    setWithBranch(true)
    setBranchName('')
    setCity('')
    setAddress('')
    setErrors({})
    if (logo) URL.revokeObjectURL(logo.previewUrl)
    setLogo(null)
    create.reset()
    upload.reset()
  }

  const submit = (event: FormEvent) => {
    event.preventDefault()
    const next: CompanyErrors = {
      ...(name.trim() ? {} : { name: t.companies.form.nameRequired }),
      ...(owner ? {} : { owner: t.companies.form.ownerRequired }),
      ...(withBranch && !branchName.trim() ? { branch: t.companies.form.branchRequired } : {}),
    }
    setErrors(next)
    if (Object.keys(next).length > 0 || !owner) return
    create.mutate(
      {
        name: name.trim(),
        ownerUserId: owner.person.id,
        ...(owner.person.name ? { ownerDisplayName: owner.person.name } : {}),
        ...(withBranch
          ? {
              branch: {
                name: branchName.trim(),
                ...(city.trim() ? { city: city.trim() } : {}),
                ...(address.trim() ? { address: address.trim() } : {}),
              },
            }
          : {}),
      },
      {
        onSuccess: (org) => {
          const done = (result: Organization, logoError: string | null) => {
            onCreated(result, logoError)
            reset()
          }
          if (!logo) return done(org, null)
          // Second call, after the company exists: a failed upload must not
          // read as a failed creation.
          upload.mutate(
            { orgId: org.id, file: logo.file },
            {
              onSuccess: (withLogo) => done(withLogo, null),
              onError: (error) => done(org, errorText(error)),
            },
          )
        },
      },
    )
  }

  return (
    <SidePanel
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next)
        if (!next) reset()
      }}
      title={t.companies.form.title}
      onSubmit={submit}
      footer={
        <>
          <Button variant="quiet" onClick={() => onOpenChange(false)}>
            {t.app.cancel}
          </Button>
          <Button type="submit" variant="primary" busy={create.isPending || upload.isPending}>
            {upload.isPending
              ? t.companies.form.uploadingLogo
              : create.isPending
                ? t.companies.form.submitting
                : t.companies.form.submit}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-7">
        <Field
          label={t.companies.form.name}
          placeholder={t.companies.form.namePlaceholder}
          value={name}
          autoFocus
          onChange={(e) => {
            setName(e.target.value)
            setErrors((prev) => ({ ...prev, name: undefined }))
          }}
          error={errors.name}
        />

        <LogoPicker
          label={t.companies.form.logo}
          labelMeta={t.companies.form.logoOptional}
          companyName={name}
          picked={logo}
          onPick={setLogo}
          onRemove={() => {
            if (logo) URL.revokeObjectURL(logo.previewUrl)
            setLogo(null)
          }}
        />

        <EmailLookup
          label={t.companies.form.owner}
          hint={t.companies.form.ownerHint}
          found={owner ? { ...owner, note: t.companies.form.ownerFoundFootnote } : null}
          onFound={(next) => {
            setOwner(next)
            setErrors((prev) => ({ ...prev, owner: undefined }))
          }}
          error={errors.owner}
          lookup={async (email) => {
            const res = await lookupForGod(email)
            return { person: res.user }
          }}
        />

        <SettingsSection
          title={t.companies.form.firstBranch}
          footer={t.companies.form.firstBranchFootnote}
          card={false}
        >
          <Checkbox checked={withBranch} onCheckedChange={setWithBranch}>
            {t.companies.form.firstBranchToggle}
          </Checkbox>
          {withBranch ? (
            <div className="flex flex-col gap-4 pt-1">
              <Field
                label={t.companies.form.branchName}
                placeholder={t.companies.form.branchNamePlaceholder}
                value={branchName}
                onChange={(e) => {
                  setBranchName(e.target.value)
                  setErrors((prev) => ({ ...prev, branch: undefined }))
                }}
                error={errors.branch}
              />
              <div className="grid grid-cols-2 gap-3">
                <Field
                  label={t.companies.form.city}
                  placeholder={t.companies.form.cityPlaceholder}
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                />
                <Field
                  label={t.companies.form.address}
                  placeholder={t.companies.form.addressPlaceholder}
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                />
              </div>
            </div>
          ) : null}
        </SettingsSection>

        {create.isError ? <InlineBanner kind="error" message={errorText(create.error)} /> : null}
      </div>
    </SidePanel>
  )
}
